/**
 * /admin — the staff console (V8 + staff-console iteration; ARCH §7).
 *
 * Four sections behind one staff-gated route: Overview (analytics snapshot,
 * GET /admin/overview), Moderation (SM-11 reports + SM-13d media), Users
 * (search / detail / restrict / ban), Verification (SM-13c queue). The API is
 * the guard (requireStaff re-reads the role per request); this screen simply
 * renders 404s for non-staff. The shell owns the overview fetch so the nav
 * badges (open reports, queue depths) stay honest across sections.
 */
import { useState } from "react";
import { copy } from "@humble/shared";
import { Button } from "../../design/index.js";
import { ModerationSection } from "./moderation.js";
import { OverviewSection, type Overview } from "./overview.js";
import { UsersSection } from "./users.js";
import { VerificationSection } from "./verification.js";
import { useLoad } from "./use-load.js";
import "./admin.css";

type Section = "overview" | "moderation" | "users" | "verification";

const SECTIONS: Array<{ key: Section; label: string }> = [
  { key: "overview", label: copy.admin.nav.overview },
  { key: "moderation", label: copy.admin.nav.moderation },
  { key: "users", label: copy.admin.nav.users },
  { key: "verification", label: copy.admin.nav.verification },
];

export function AdminScreen() {
  const [section, setSection] = useState<Section>("overview");
  const overview = useLoad<Overview>("/admin/overview");

  const badges: Partial<Record<Section, number>> = overview.data
    ? {
        moderation:
          overview.data.moderation.reports_open +
          overview.data.moderation.media_scanning +
          overview.data.moderation.media_quarantined,
        verification: overview.data.moderation.verification_pending,
      }
    : {};

  const open = (s: Section) => {
    setSection(s);
    if (s === "overview") overview.reload(); // fresh numbers on return
  };

  return (
    <div className="admin admin--console">
      <header className="admin__head">
        <p className="h-caps admin__kicker">{copy.admin.kicker}</p>
        <h1 className="type-display-3">{copy.admin.console_title}</h1>
      </header>

      <nav className="admin__nav" data-testid="admin-nav" aria-label="Console sections">
        {SECTIONS.map(({ key, label }) => (
          <Button
            key={key}
            variant={section === key ? "primary" : "ghost"}
            size="sm"
            onClick={() => open(key)}
            aria-current={section === key ? "page" : undefined}
          >
            {label}
            {(badges[key] ?? 0) > 0 && <span className="admin__nav-badge">{badges[key]}</span>}
          </Button>
        ))}
      </nav>

      <section aria-live="polite">
        <h2 className="admin__section-title">{SECTIONS.find((s) => s.key === section)!.label}</h2>
        {section === "overview" && (
          <OverviewSection overview={overview} onNavigate={(s) => setSection(s)} />
        )}
        {section === "moderation" && <ModerationSection />}
        {section === "users" && <UsersSection />}
        {section === "verification" && <VerificationSection />}
      </section>
    </div>
  );
}
